import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { 
  LogOut, 
  Play, 
  BookOpen, 
  TrendingUp,
  TrendingDown,
  Users,
  AlertTriangle,
  Target,
  DollarSign,
  Lightbulb,
  Share2,
  MessageSquare,
  Bell,
  AlertCircle,
  Clock,
  Ticket,
  Heart,
  Gift,
  Calendar,
  Save,
  RotateCcw,
  History,
  X,
  MapPin,
  Settings,
  User,
  Check,
  PhoneCall,
  Ban,
  HelpCircle,
  DoorClosed,
  ChevronDown,
  ChevronUp,
  Activity
} from "lucide-react";
import { toast } from "sonner";
import TapTrackLogo from "../components/TapTrackLogo";
import { GiftAidMetricsStrip } from "../components/GiftAidMetricsStrip";
import { PassportScoreCard } from "../components/PassportScoreCard";
import { PassportAlertBanner } from "../components/PassportAlertBanner";
import { ContextSwitcher } from "../components/ContextSwitcher";
import DualViewSwitcher from "../components/DualViewSwitcher";
import PaymentReadinessBanner from "../components/PaymentReadinessBanner";

// Interaction stages - simple counter system
const STAGES = [
  { id: "intro", label: "Intro" },
  { id: "explanation", label: "Explanation" },
  { id: "ask", label: "Ask" },
  { id: "objection", label: "Objection" },
  { id: "reclose", label: "Re-close" }
];

// Debounce helper
const useDebounce = (callback, delay) => {
  const lastCall = useRef(0);
  
  return useCallback((...args) => {
    const now = Date.now();
    if (now - lastCall.current >= delay) {
      lastCall.current = now;
      callback(...args);
    }
  }, [callback, delay]);
};

export default function FundraiserDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [loading, setLoading] = useState(true);
  const [guidanceMessages, setGuidanceMessages] = useState([]);
  const [acknowledgingGuidance, setAcknowledgingGuidance] = useState(null);
  
  // Stage counter state
  const [sessionStageCounts, setSessionStageCounts] = useState({
    intro: 0, explanation: 0, ask: 0, objection: 0, reclose: 0
  });
  const [sessionConversions, setSessionConversions] = useState({
    lottery_single: 0, lottery_double: 0, regular_giving: 0, one_off: 0
  });
  const [sessionDate, setSessionDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [loggingStage, setLoggingStage] = useState(null);
  const [activeInteractionId, setActiveInteractionId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastClickTime, setLastClickTime] = useState(0);  // HARD LOCK: Prevent double counting
  
  // Location state
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  
  // Session management state
  const [savingSession, setSavingSession] = useState(false);
  const [showSessionHistory, setShowSessionHistory] = useState(false);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [historyDateRange, setHistoryDateRange] = useState(7); // Default last 7 days
  const [historyStartDate, setHistoryStartDate] = useState("");
  const [historyEndDate, setHistoryEndDate] = useState("");
  
  // Today's Schedule state
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [loadingTodaySchedule, setLoadingTodaySchedule] = useState(true);

  // Upcoming 14-day schedule state
  const [upcomingSchedule, setUpcomingSchedule] = useState([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(true);
  
  // Sync state
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncing, setSyncing] = useState(false);
  
  // Donation type selection modal
  const [showDonationTypeModal, setShowDonationTypeModal] = useState(false);
  
  // Today panel detail modal
  const [todayDetailType, setTodayDetailType] = useState(null);

  // State for current session ID
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [loadingSession, setLoadingSession] = useState(false);

  // Organisation branding state (for white-labelling)
  const [orgBranding, setOrgBranding] = useState({ orgLogoUrl: null, orgDisplayName: "Engage" });

  // Working preferences state
  const [workingPreferences, setWorkingPreferences] = useState({});
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);

  // My Sign-Ups state
  const [mySignups, setMySignups] = useState([]);
  const [loadingSignups, setLoadingSignups] = useState(false);
  const [showSignups, setShowSignups] = useState(false);

  // My Availability state
  const [myAvailability, setMyAvailability] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [showAvailability, setShowAvailability] = useState(false);
  const [availMonth, setAvailMonth] = useState(0); // 0, 1, 2 for 3 months
  const [weekPattern, setWeekPattern] = useState({});  // dayOfWeek (0-6) -> true/false/undefined
  const [applyingPattern, setApplyingPattern] = useState(false);

  // My Pay state
  const [myPayRecords, setMyPayRecords] = useState([]);
  const [loadingPay, setLoadingPay] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [loadingPayslip, setLoadingPayslip] = useState(false);

  // Knock Sheet state
  const [knockRoad, setKnockRoad] = useState("");
  const [knockHouse, setKnockHouse] = useState("");
  const [knockRoads, setKnockRoads] = useState([]);
  const [knockList, setKnockList] = useState([]);
  const [knockCallbacks, setKnockCallbacks] = useState([]);
  const [knockSaving, setKnockSaving] = useState(false);
  const [knockFlash, setKnockFlash] = useState(null); // "success" | null
  const [knockShowCallbackOptions, setKnockShowCallbackOptions] = useState(false);
  const [knockShowOtherInput, setKnockShowOtherInput] = useState(false);
  const [knockOtherNote, setKnockOtherNote] = useState("");
  const [knockShowList, setKnockShowList] = useState(false);
  const [knockShowCallbacks, setKnockShowCallbacks] = useState(false);
  const [knockGuidance, setKnockGuidance] = useState(null);

  // Days of the week for preferences
  const DAYS_OF_WEEK = [
    { id: 0, label: "Monday", short: "Mon" },
    { id: 1, label: "Tuesday", short: "Tue" },
    { id: 2, label: "Wednesday", short: "Wed" },
    { id: 3, label: "Thursday", short: "Thu" },
    { id: 4, label: "Friday", short: "Fri" },
    { id: 5, label: "Saturday", short: "Sat" },
    { id: 6, label: "Sunday", short: "Sun" }
  ];

  // Auto midnight reset - check if session date is stale
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    if (sessionDate !== today) {
      setSessionStageCounts({ intro: 0, explanation: 0, ask: 0, objection: 0, reclose: 0 });
      setSessionConversions({ lottery_single: 0, lottery_double: 0, regular_giving: 0, one_off: 0 });
      setSessionDate(today);
      setActiveInteractionId(null);
      setCurrentSessionId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionDate]);

  useEffect(() => {
    document.title = "Engage — Fundraiser";
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-sync interval: fetch setting, start/stop interval, cleanup on unmount
  const syncIntervalRef = useRef(null);
  useEffect(() => {
    let mounted = true;
    const setupAutoSync = async () => {
      try {
        const res = await apiClient.get("/fundraiser/settings");
        const interval = res.data?.syncIntervalMinutes;
        if (interval && mounted) {
          if (syncIntervalRef.current) clearInterval(syncIntervalRef.current);
          syncIntervalRef.current = setInterval(async () => {
            try {
              let syncData = { campaignId: null, source: "auto" };
              if (navigator.geolocation) {
                try {
                  const pos = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 }));
                  syncData.latitude = pos.coords.latitude;
                  syncData.longitude = pos.coords.longitude;
                } catch (e) { /* no geo */ }
              }
              await apiClient.post("/sync", syncData);
            } catch (e) { /* silent auto-sync failure */ }
          }, interval * 60 * 1000);
        }
      } catch (e) { /* no settings */ }
    };
    setupAutoSync();
    return () => {
      mounted = false;
      if (syncIntervalRef.current) clearInterval(syncIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load existing session when campaign or location changes
  const loadExistingSession = async (campaignId, locationId) => {
    if (!campaignId || !locationId) return;
    
    setLoadingSession(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await apiClient.get(`/tracker/sessions/today?campaignId=${campaignId}&locationId=${locationId}`);
      
      if (res.data && res.data.date === today) {
        // Load existing session data
        setSessionStageCounts(res.data.stageCounts || { intro: 0, explanation: 0, ask: 0, objection: 0, reclose: 0 });
        setSessionConversions(res.data.conversions || { lottery_single: 0, lottery_double: 0, regular_giving: 0, one_off: 0 });
        setCurrentSessionId(res.data.id);
        setSessionDate(res.data.date);
      } else {
        // No existing session - start fresh
        setSessionStageCounts({ intro: 0, explanation: 0, ask: 0, objection: 0, reclose: 0 });
        setSessionConversions({ lottery_single: 0, lottery_double: 0, regular_giving: 0, one_off: 0 });
        setCurrentSessionId(null);
      }
    } catch (error) {
      // No session found - start fresh
      setSessionStageCounts({ intro: 0, explanation: 0, ask: 0, objection: 0, reclose: 0 });
      setSessionConversions({ lottery_single: 0, lottery_double: 0, regular_giving: 0, one_off: 0 });
      setCurrentSessionId(null);
    } finally {
      setLoadingSession(false);
    }
  };

  // Handle location change - auto-save current session, load new
  const handleLocationChange = async (newLocationId) => {
    // Auto-save current session if it has data
    if (selectedLocation && sessionStageCounts.intro > 0) {
      try {
        await apiClient.post("/tracker/sessions", {
          campaignId: selectedCampaign,
          locationId: selectedLocation,
          date: sessionDate,
          stageCounts: sessionStageCounts,
          conversions: sessionConversions
        });
      } catch (error) {
        toast.error("Failed to save session data");
      }
    }
    
    // Update location
    setSelectedLocation(newLocationId);
    setActiveInteractionId(null);
    
    // Load session for new location
    if (selectedCampaign && newLocationId) {
      await loadExistingSession(selectedCampaign, newLocationId);
    }
  };

  const loadData = async () => {
    try {
      const [metricsRes, campaignsRes, guidanceRes, locationsRes, brandingRes, scheduleRes, prefsRes, upcomingRes] = await Promise.all([
        apiClient.get("/metrics/fundraiser").catch(() => ({ data: null })),
        apiClient.get("/campaigns").catch(() => ({ data: [] })),
        apiClient.get("/guidance/my").catch(() => ({ data: [] })),
        apiClient.get("/locations").catch(() => ({ data: [] })),
        apiClient.get("/organisation/branding").catch(() => ({ data: { orgLogoUrl: null, orgDisplayName: "Engage" } })),
        apiClient.get("/schedules/today").catch(() => ({ data: [] })),
        apiClient.get("/preferences/working").catch(() => ({ data: [] })),
        apiClient.get("/schedules/upcoming").catch(() => ({ data: [] }))
      ]);
      setMetrics(metricsRes.data);
      setCampaigns(campaignsRes.data || []);
      setGuidanceMessages(guidanceRes.data || []);
      setLocations(locationsRes.data || []);
      setOrgBranding({
        orgLogoUrl: brandingRes.data?.orgLogoUrl || null,
        orgDisplayName: brandingRes.data?.orgDisplayName || "Engage"
      });
      setTodaySchedule(scheduleRes.data || []);
      setLoadingTodaySchedule(false);
      setUpcomingSchedule(upcomingRes.data || []);
      setLoadingUpcoming(false);
      
      // Load working preferences into state object
      const prefsObj = {};
      (prefsRes.data || []).forEach(p => {
        prefsObj[p.dayOfWeek] = p.preferenceType;
      });
      setWorkingPreferences(prefsObj);
      
      // Set defaults and load existing session
      const defaultCampaign = (campaignsRes.data && campaignsRes.data.length > 0) ? campaignsRes.data[0].id : null;
      const defaultLocation = (locationsRes.data && locationsRes.data.length > 0) ? locationsRes.data[0].id : null;
      
      if (defaultCampaign && !selectedCampaign) {
        setSelectedCampaign(defaultCampaign);
      }
      if (defaultLocation && !selectedLocation) {
        setSelectedLocation(defaultLocation);
      }
      
      // Load existing session for today
      const campId = selectedCampaign || defaultCampaign;
      const locId = selectedLocation || defaultLocation;
      if (campId && locId) {
        await loadExistingSession(campId, locId);
      }
    } catch (error) {
      console.error("Dashboard load error", error);
    } finally {
      setLoading(false);
    }
  };

  // Save working preferences
  const saveWorkingPreferences = async () => {
    setSavingPreferences(true);
    try {
      const prefsArray = Object.entries(workingPreferences)
        .filter(([, value]) => value)  // Only include set preferences
        .map(([dayOfWeek, preferenceType]) => ({
          dayOfWeek: parseInt(dayOfWeek),
          preferenceType
        }));
      
      await apiClient.put("/preferences/working", prefsArray);
      toast.success("Working preferences saved");
      setShowPreferencesModal(false);
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setSavingPreferences(false);
    }
  };

  // Toggle preference for a day
  const toggleDayPreference = (dayId, type) => {
    setWorkingPreferences(prev => {
      const current = prev[dayId];
      // If clicking same type, clear it; otherwise set it
      if (current === type) {
        const { [dayId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [dayId]: type };
    });
  };

  // Load My Sign-Ups
  const loadMySignups = async () => {
    setLoadingSignups(true);
    try {
      const res = await apiClient.get("/fundraiser/signups");
      setMySignups(res.data || []);
    } catch (e) {
    } finally {
      setLoadingSignups(false);
    }
  };

  // Load My Availability
  const loadMyAvailability = async () => {
    setLoadingAvailability(true);
    try {
      const res = await apiClient.get("/availability");
      setMyAvailability(res.data || []);
    } catch (e) {
    } finally {
      setLoadingAvailability(false);
    }
  };

  // Load My Pay records
  const loadMyPay = async () => {
    setLoadingPay(true);
    try {
      const res = await apiClient.get("/fundraiser/payroll");
      setMyPayRecords(res.data || []);
    } catch (e) {
      setMyPayRecords([]);
    } finally {
      setLoadingPay(false);
    }
  };

  const loadPayslipDetail = async (recordId) => {
    setLoadingPayslip(true);
    try {
      const res = await apiClient.get(`/fundraiser/payroll/${recordId}`);
      setSelectedPayslip(res.data);
    } catch (e) {
      toast.error("Failed to load payslip");
    } finally {
      setLoadingPayslip(false);
    }
  };

  // Toggle availability for a date
  const toggleAvailability = async (dateStr) => {
    const existing = myAvailability.find(a => a.date === dateStr);
    const newAvail = !existing?.available;
    try {
      await apiClient.put("/availability", { date: dateStr, available: newAvail });
      setMyAvailability(prev => {
        const filtered = prev.filter(a => a.date !== dateStr);
        return [...filtered, { date: dateStr, available: newAvail, fundraiserId: user?.id }];
      });
    } catch (e) {
      toast.error("Failed to update availability");
    }
  };

  // Apply week pattern to remaining quarter
  const applyWeekPattern = async () => {
    const activeDays = Object.entries(weekPattern).filter(([, v]) => v !== undefined);
    if (activeDays.length === 0) {
      toast.error("Set at least one day in the pattern");
      return;
    }
    setApplyingPattern(true);
    try {
      const today = new Date();
      const todayStr = today.toISOString().split("T")[0];
      // Generate dates for the 3-month window from today
      const dates = [];
      for (let offset = 0; offset < 3; offset++) {
        const cal = getCalendarDays(offset);
        cal.days.forEach(dateStr => {
          if (dateStr < todayStr) return; // skip past dates
          const d = new Date(dateStr);
          const dow = (d.getDay() + 6) % 7; // 0=Mon
          const patternValue = weekPattern[dow];
          if (patternValue !== undefined) {
            dates.push({ date: dateStr, available: patternValue });
          }
        });
      }
      if (dates.length === 0) {
        toast.error("No future dates match the pattern");
        setApplyingPattern(false);
        return;
      }
      const res = await apiClient.post("/availability/batch", { dates });
      toast.success(`Pattern applied to ${res.data.updated} days`);
      // Refresh availability
      loadMyAvailability();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to apply pattern");
    } finally {
      setApplyingPattern(false);
    }
  };

  // Get calendar days for a month offset
  const getMonthDays = (monthOffset) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + monthOffset;
    const d = new Date(year, month, 1);
    const days = [];
    while (d.getMonth() === (month % 12 + 12) % 12 || (month >= 12 && d.getMonth() === month - 12)) {
      days.push(d.toISOString().split("T")[0]);
      d.setDate(d.getDate() + 1);
      if (days.length > 35) break; // safety
    }
    // Simpler approach
    return days;
  };

  const getCalendarDays = (monthOffset) => {
    const now = new Date();
    const targetDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      days.push(date.toISOString().split("T")[0]);
    }
    return { days, label: targetDate.toLocaleDateString("en-GB", { month: "long", year: "numeric" }) };
  };

  const acknowledgeGuidance = async (guidanceId) => {
    setAcknowledgingGuidance(guidanceId);
    try {
      await apiClient.post(`/guidance/${guidanceId}/acknowledge`);
      toast.success("Guidance acknowledged");
      const guidanceRes = await apiClient.get("/guidance/my");
      setGuidanceMessages(guidanceRes.data || []);
    } catch (error) {
      toast.error("Failed to acknowledge guidance");
    } finally {
      setAcknowledgingGuidance(null);
    }
  };

  // Simple stage click handler - increments ONLY that stage
  const handleStageClick = (stageId) => {
    // Prevent rapid double-clicks (500ms cooldown)
    const now = Date.now();
    if (now - lastClickTime < 500) {
      return;
    }
    setLastClickTime(now);
    
    // Must have campaign and location selected
    if (!selectedCampaign) {
      toast.error("Please select a campaign first");
      return;
    }
    if (!selectedLocation) {
      toast.error("Please select a location first");
      return;
    }
    
    // Increment ONLY the clicked stage - no other stages affected
    setSessionStageCounts(prev => ({
      ...prev,
      [stageId]: prev[stageId] + 1
    }));
  };
  // Sync Now function
  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      let syncData = { campaignId: selectedCampaign || null, source: "manual" };
      
      // Get geolocation if available
      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          syncData.latitude = position.coords.latitude;
          syncData.longitude = position.coords.longitude;
        } catch (geoError) {
          // Geolocation failed - continue without it
        }
      }
      
      const response = await apiClient.post("/sync", syncData);
      setLastSyncTime(response.data.timestamp);
      toast.success(`Synced at ${new Date(response.data.timestamp).toLocaleTimeString()}`);
    } catch (error) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  // Save Session function - updates existing or creates new, auto-resets after save
  const handleSaveSession = async () => {
    if (!selectedCampaign) {
      toast.error("Please select a campaign first");
      return;
    }
    
    if (!selectedLocation) {
      toast.error("Please select a location first");
      return;
    }
    
    setSavingSession(true);
    try {
      const res = await apiClient.post("/tracker/sessions", {
        campaignId: selectedCampaign,
        locationId: selectedLocation,
        date: sessionDate,
        stageCounts: sessionStageCounts,
        conversions: sessionConversions
      });
      setCurrentSessionId(res.data.id);
      toast.success("Session saved successfully");
      
      // Auto-reset tracker state after successful save
      setSessionStageCounts({ intro: 0, explanation: 0, ask: 0, objection: 0, reclose: 0 });
      setSessionConversions({ lottery_single: 0, lottery_double: 0, regular_giving: 0, one_off: 0 });
      setActiveInteractionId(null);
      setCurrentSessionId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save session");
    } finally {
      setSavingSession(false);
    }
  };

  // Load session history
  const loadSessionHistory = async () => {
    try {
      const res = await apiClient.get("/tracker/sessions");
      setSessionHistory(res.data || []);
    } catch (error) {
      // Return empty array instead of error if no history
      setSessionHistory([]);
    }
  };

  // ==================== KNOCK SHEET FUNCTIONS ====================
  const loadKnockData = useCallback(async (locId) => {
    if (!locId) return;
    try {
      const [roadsRes, listRes, cbRes, guidanceRes] = await Promise.all([
        apiClient.get(`/knock/roads?locationId=${locId}`).catch(() => ({ data: [] })),
        apiClient.get(`/knock/list?locationId=${locId}`).catch(() => ({ data: [] })),
        apiClient.get("/knock/callbacks").catch(() => ({ data: [] })),
        apiClient.get(`/knock/guidance?locationId=${locId}`).catch(() => ({ data: null }))
      ]);
      setKnockRoads(roadsRes.data || []);
      setKnockList(listRes.data || []);
      setKnockCallbacks(cbRes.data || []);
      setKnockGuidance(guidanceRes.data || null);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    if (selectedLocation) loadKnockData(selectedLocation);
  }, [selectedLocation, loadKnockData]);

  const handleKnock = async (outcome, extra = {}) => {
    if (!selectedCampaign || !selectedLocation || !knockRoad.trim()) {
      toast.error("Select location and enter road name");
      return;
    }
    setKnockSaving(true);
    try {
      await apiClient.post("/knock", {
        locationId: selectedLocation,
        campaignId: selectedCampaign,
        road: knockRoad.trim(),
        houseNumber: knockHouse.trim(),
        outcome,
        ...extra
      });
      // Flash confirmation
      setKnockFlash("success");
      setTimeout(() => setKnockFlash(null), 600);
      // Clear house only (keep road for next house on same street)
      setKnockHouse("");
      setKnockShowCallbackOptions(false);
      setKnockShowOtherInput(false);
      setKnockOtherNote("");
      // Refresh knock data
      loadKnockData(selectedLocation);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setKnockSaving(false);
    }
  };

  const handleCallbackQuick = (option) => {
    const now = new Date();
    let dt;
    if (option === "later") {
      dt = new Date(now.getTime() + 3 * 60 * 60 * 1000); // +3 hours
    } else if (option === "tomorrow") {
      dt = new Date(now);
      dt.setDate(dt.getDate() + 1);
      dt.setHours(10, 0, 0, 0);
    }
    handleKnock("callback", { callbackDateTime: dt ? dt.toISOString() : null });
  };

  const handleDoNotKnock = async (knockId) => {
    try {
      await apiClient.put(`/knock/${knockId}/do-not-knock`, { doNotKnock: true });
      toast.success("Marked as Do Not Knock");
      loadKnockData(selectedLocation);
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const filteredRoads = knockRoad.trim()
    ? knockRoads.filter(r => r.toLowerCase().includes(knockRoad.toLowerCase()))
    : [];

  // Group sessions by date for display
  const getGroupedSessions = () => {
    if (!sessionHistory.length) return [];
    
    // Filter by date range
    const now = new Date();
    let filteredSessions = sessionHistory;
    
    if (historyStartDate && historyEndDate) {
      // Custom date range
      filteredSessions = sessionHistory.filter(s => {
        const sessionDate = new Date(s.date);
        return sessionDate >= new Date(historyStartDate) && sessionDate <= new Date(historyEndDate);
      });
    } else {
      // Last N days
      const cutoffDate = new Date(now);
      cutoffDate.setDate(cutoffDate.getDate() - historyDateRange);
      filteredSessions = sessionHistory.filter(s => new Date(s.date) >= cutoffDate);
    }
    
    // Group by date
    const grouped = {};
    filteredSessions.forEach(session => {
      const dateKey = session.date;
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: dateKey,
          sessions: [],
          totalInteractions: 0,
          totalConversions: 0
        };
      }
      grouped[dateKey].sessions.push(session);
      grouped[dateKey].totalInteractions += session.totalInteractions || 0;
      grouped[dateKey].totalConversions += session.totalConversions || 0;
    });
    
    // Sort days newest first, sessions within each day newest first
    return Object.values(grouped)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .map(day => ({
        ...day,
        conversionRate: day.totalInteractions > 0 
          ? ((day.totalConversions / day.totalInteractions) * 100).toFixed(1)
          : "0.0",
        sessions: day.sessions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      }));
  };

  // Format time from ISO string
  const formatSessionTime = (createdAt) => {
    return new Date(createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  // View Sessions handler
  const handleViewSessions = () => {
    loadSessionHistory();
    setShowSessionHistory(true);
  };

  // Start conversion with type pre-selection
  const handleStartClick = () => {
    if (!selectedCampaign) {
      toast.error("Please select a campaign");
      return;
    }
    setShowDonationTypeModal(true);
  };

  const startConversionWithType = async (donationType) => {
    setShowDonationTypeModal(false);
    if (!selectedLocation) {
      toast.error("Please select a location first");
      return;
    }
    try {
      const response = await apiClient.post("/interactions", { 
        campaignId: selectedCampaign,
        locationId: selectedLocation 
      });
      // Navigate with donation type pre-selected
      navigate(`/conversion/${response.data.id}?type=${donationType}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start conversion");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const unreadGuidanceCount = guidanceMessages.filter(g => !g.acknowledged).length;

  // === CURRENT SESSION metrics (Tracker scope) ===
  const stageCounts = sessionStageCounts;
  const sessionConversionTotal = Object.values(sessionConversions).reduce((a, b) => a + b, 0);
  const sessionStageTotal = Object.values(stageCounts).reduce((a, b) => a + b, 0);
  const sessionTotalInteractions = sessionStageTotal + sessionConversionTotal;
  const sessionConversionRate = sessionTotalInteractions > 0 
    ? ((sessionConversionTotal / sessionTotalInteractions) * 100).toFixed(0) 
    : 0;
  const sessionRatio = sessionConversionTotal > 0 
    ? Math.round(sessionTotalInteractions / sessionConversionTotal) 
    : 0;

  // === TODAY'S PERFORMANCE metrics (all saved sessions today + current live session) ===
  const todayTotalInteractions = (metrics?.todayInteractions || 0) + sessionStageTotal;
  const todayTotalConversions = (metrics?.todayConversions || 0) + sessionConversionTotal;
  const todayConversionRate = todayTotalInteractions > 0
    ? ((todayTotalConversions / todayTotalInteractions) * 100).toFixed(1)
    : 0;

  // Get selected campaign object for pricing
  const selectedCampaignObj = campaigns.find(c => c.id === selectedCampaign);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-600">Loading dashboard...</div>
      </div>
    );
  }

  // Guard: fundraiser with no organisation
  if (!user?.organisationId && user?.role === "fundraiser") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="no-org-screen">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-8 w-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">No Organisation Assigned</h2>
          <p className="text-slate-500 mb-6">You are not currently assigned to an organisation. Please contact your manager or platform administrator.</p>
          <Button onClick={logout} variant="outline" className="gap-2">Log Out</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="fundraiser-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Organisation Branding - Show org logo/name if available, else Engage */}
            {orgBranding.orgLogoUrl ? (
              <div className="flex items-center gap-2" data-testid="org-branding">
                <img 
                  src={orgBranding.orgLogoUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${orgBranding.orgLogoUrl}` : orgBranding.orgLogoUrl}
                  alt={orgBranding.orgDisplayName}
                  className="h-8 w-8 object-contain rounded"
                />
                <span className="font-heading font-bold text-lg text-slate-900 hidden sm:inline">
                  {orgBranding.orgDisplayName}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2" data-testid="taptrack-branding">
                <TapTrackLogo size="default" />
                {orgBranding.orgDisplayName && orgBranding.orgDisplayName !== "Engage" && (
                  <span className="font-heading font-bold text-lg text-slate-900 hidden sm:inline">
                    {orgBranding.orgDisplayName}
                  </span>
                )}
              </div>
            )}
            {unreadGuidanceCount > 0 && (
              <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 rounded-full" data-testid="guidance-badge">
                <Bell className="h-3 w-3 text-amber-600" />
                <span className="text-xs font-bold text-amber-700">{unreadGuidanceCount}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Profile Avatar */}
            <div className="h-9 w-9 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0" data-testid="fundraiser-avatar">
              {user?.profileImageUrl ? (
                <img 
                  src={user.profileImageUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${user.profileImageUrl}?auth=${encodeURIComponent(sessionStorage.getItem("taptrack_token") || "")}` : user.profileImageUrl}
                  alt={user.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-sm font-bold text-slate-500">{user?.name?.charAt(0)?.toUpperCase() || <User className="h-4 w-4 text-slate-400" />}</span>
              )}
            </div>
            <span className="hidden sm:inline text-sm font-medium text-slate-700 mr-1" data-testid="fundraiser-name">{user?.name}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/schedule")}
              className="h-9 px-3 border-slate-200"
              data-testid="preferences-btn"
            >
              <Settings className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">My Schedule</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/records")}
              className="h-9 px-3 border-slate-200"
              data-testid="records-btn"
            >
              <TrendingUp className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">My Records</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/training")}
              className="h-9 px-3 border-slate-200"
              data-testid="training-btn"
            >
              <BookOpen className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Training</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/operational-record")}
              className="h-9 px-3 border-emerald-200 text-emerald-700"
              data-testid="op-record-entry-btn"
            >
              <Activity className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">My Performance Record</span>
            </Button>
            <DualViewSwitcher />
            <ContextSwitcher compact />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="h-9 px-3 text-slate-500"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* OCI V2.24B — primary CTA at top of fundraiser workspace */}
        <Card className="border-slate-200 shadow-none bg-gradient-to-br from-emerald-500 to-emerald-600" data-testid="open-form-card">
          <CardContent className="p-6">
            <h2 className="font-heading font-bold text-lg text-white mb-4">Open Form</h2>
            <div className="flex flex-col sm:flex-row gap-4">
              <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
                <SelectTrigger className="h-14 bg-white/10 border-white/20 text-white placeholder:text-white/60 flex-1" data-testid="campaign-select">
                  <SelectValue placeholder="Select campaign" />
                </SelectTrigger>
                <SelectContent>
                  {campaigns.map((campaign) => (
                    <SelectItem key={campaign.id} value={campaign.id}>{campaign.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-3">
                <Button
                  onClick={handleStartClick}
                  disabled={!selectedCampaign}
                  className="h-14 px-8 bg-white text-emerald-600 hover:bg-white/90 font-semibold rounded-xl active:scale-[0.98] flex-1 sm:flex-none"
                  data-testid="start-conversion-btn"
                >
                  <Play className="h-5 w-5 mr-2" />
                  Open Form
                </Button>
                <Button
                  onClick={() => selectedCampaign && navigate(`/share/${selectedCampaign}`)}
                  disabled={!selectedCampaign}
                  variant="outline"
                  className="h-14 px-5 bg-white/10 border-white/20 text-white hover:bg-white/20 font-semibold rounded-xl"
                  data-testid="share-btn"
                >
                  <Share2 className="h-5 w-5 mr-2" />
                  <span className="hidden sm:inline">Share Link</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Today's Assignment */}
        <Card className="border-blue-200 bg-blue-50/50 shadow-none" data-testid="today-assignment-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="h-5 w-5 text-blue-600" />
              <h3 className="font-heading font-bold text-slate-900">Today's Assignment</h3>
            </div>
            {loadingTodaySchedule ? (
              <div className="text-sm text-slate-500">Loading...</div>
            ) : todaySchedule.length === 0 ? (
              <div className="text-sm text-slate-500 italic">No assignment today</div>
            ) : (
              <div className="space-y-2">
                {todaySchedule.map((shift, idx) => (
                  <div 
                    key={shift.id || `shift-${idx}`} 
                    className="flex items-center gap-3 p-3 bg-white rounded-lg border border-blue-200"
                  >
                    <div className="text-sm font-mono font-medium text-blue-700 bg-blue-100 px-2 py-1 rounded">
                      {shift.startTime}–{shift.endTime}
                    </div>
                    <div>
                      <div className="flex items-center gap-1 text-sm font-medium text-slate-900">
                        <MapPin className="h-3.5 w-3.5 text-slate-500" />
                        {shift.locationName}
                      </div>
                      {shift.campaignName && (
                        <div className="text-xs text-violet-600">{shift.campaignName}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Schedule (Next 2 Weeks) */}
        <Card className="border-slate-200 shadow-none" data-testid="upcoming-schedule-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="h-5 w-5 text-slate-600" />
              <h3 className="font-heading font-bold text-slate-900">Your Schedule (Next 2 Weeks)</h3>
            </div>
            {loadingUpcoming ? (
              <div className="text-sm text-slate-500">Loading...</div>
            ) : upcomingSchedule.length === 0 ? (
              <div className="text-sm text-slate-500 italic" data-testid="upcoming-empty-state">No shifts scheduled for the next 2 weeks</div>
            ) : (
              <div className="space-y-1">
                {(() => {
                  const grouped = {};
                  upcomingSchedule.forEach(s => {
                    if (!grouped[s.date]) grouped[s.date] = [];
                    grouped[s.date].push(s);
                  });
                  const dayNames = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
                  return Object.entries(grouped).map(([dateStr, shifts]) => {
                    const d = new Date(dateStr + "T00:00:00");
                    const dayLabel = `${dayNames[d.getDay()]} ${d.getDate()} ${d.toLocaleDateString("en-GB", { month: "short" })}`;
                    const isToday = dateStr === new Date().toISOString().split("T")[0];
                    return (
                      <div key={dateStr} className={`rounded-lg border p-3 ${isToday ? "border-blue-300 bg-blue-50/50" : "border-slate-100 bg-white"}`} data-testid={`upcoming-day-${dateStr}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-semibold text-slate-800">{dayLabel}</span>
                          {isToday && <span className="text-[10px] font-semibold bg-blue-600 text-white px-1.5 py-0.5 rounded">Today</span>}
                        </div>
                        <div className="space-y-1.5">
                          {shifts.map(shift => (
                            <div key={shift.id} className="flex items-start gap-3 pl-2" data-testid={`upcoming-shift-${shift.id}`}>
                              <span className="text-xs font-mono text-slate-500 mt-0.5 whitespace-nowrap">{shift.startTime}–{shift.endTime}</span>
                              <div className="min-w-0">
                                <div className="flex items-center gap-1 text-sm text-slate-900">
                                  <MapPin className="h-3 w-3 text-slate-400 flex-shrink-0" />
                                  <span className="truncate">{shift.locationName}</span>
                                </div>
                                {shift.campaignName && (
                                  <div className="text-xs text-violet-600 pl-4">{shift.campaignName}</div>
                                )}
                                {shift.teammates.length > 0 && (
                                  <div className="text-xs text-slate-400 pl-4">
                                    with {shift.teammates.join(", ")}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Next Best Action */}
        {selectedLocation && knockGuidance && (knockGuidance.nextCallback || knockGuidance.recommendedRoad || knockGuidance.lowActivity?.length > 0) && (
          <Card className="border-emerald-200 bg-emerald-50/30 shadow-none" data-testid="next-best-action-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Target className="h-5 w-5 text-emerald-600" />
                <h3 className="font-heading font-bold text-slate-900">Next Best Action</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Next Callback */}
                <div className="p-3 bg-white rounded-xl border border-slate-100" data-testid="nba-callback">
                  <div className="text-xs font-semibold text-amber-600 mb-1 uppercase tracking-wide">Next Callback</div>
                  {knockGuidance.nextCallback ? (
                    <div>
                      <div className="font-bold text-slate-900 text-sm">{knockGuidance.nextCallback.road} {knockGuidance.nextCallback.houseNumber}</div>
                      <div className="text-xs text-slate-500">{knockGuidance.nextCallback.callbackDateTime ? new Date(knockGuidance.nextCallback.callbackDateTime).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</div>
                    </div>
                  ) : (
                    <div className="text-xs text-slate-400 italic">No callbacks due</div>
                  )}
                </div>
                {/* Recommended Road */}
                <div className="p-3 bg-white rounded-xl border border-slate-100" data-testid="nba-recommended-road">
                  <div className="text-xs font-semibold text-emerald-600 mb-1 uppercase tracking-wide">Best Road</div>
                  {knockGuidance.recommendedRoad ? (
                    <div>
                      <div className="font-bold text-slate-900 text-sm">{knockGuidance.recommendedRoad.road}</div>
                      <div className="text-xs text-slate-500">{(knockGuidance.recommendedRoad.rate * 100).toFixed(0)}% rate ({knockGuidance.recommendedRoad.conversions}/{knockGuidance.recommendedRoad.visits})</div>
                    </div>
                  ) : (
                    <div className="text-xs text-slate-400 italic">Keep logging to unlock</div>
                  )}
                </div>
                {/* Low Activity */}
                <div className="p-3 bg-white rounded-xl border border-slate-100" data-testid="nba-low-activity">
                  <div className="text-xs font-semibold text-blue-600 mb-1 uppercase tracking-wide">Try These Roads</div>
                  {knockGuidance.lowActivity?.length > 0 ? (
                    <div className="space-y-0.5">
                      {knockGuidance.lowActivity.slice(0, 3).map((la, i) => (
                        <button key={i} className="block text-sm font-medium text-slate-800 hover:text-blue-600 cursor-pointer" onClick={() => setKnockRoad(la.road)}>
                          {la.road} <span className="text-[10px] text-slate-400">({la.visits} visits)</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-slate-400 italic">All roads covered recently</div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Guidance Messages */}
        {guidanceMessages.filter(g => !g.acknowledged).length > 0 && (
          <Card className="border-amber-200 bg-amber-50 shadow-none" data-testid="guidance-card">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="h-5 w-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Bell className="h-4 w-4 text-amber-600" />
                    <h3 className="font-heading font-bold text-slate-900">Guidance from your Manager</h3>
                  </div>
                  {guidanceMessages.filter(g => !g.acknowledged).map((guidance) => (
                    <div key={guidance.id} className="mt-3 p-3 bg-white rounded-lg border border-amber-200">
                      <p className="text-slate-700 mb-3">{guidance.message}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400">
                          {new Date(guidance.createdAt).toLocaleDateString()}
                        </span>
                        <Button
                          size="sm"
                          onClick={() => acknowledgeGuidance(guidance.id)}
                          disabled={acknowledgingGuidance === guidance.id}
                          className="h-8 bg-amber-500 hover:bg-amber-600"
                        >
                          {acknowledgingGuidance === guidance.id ? (
                            <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : "Acknowledge"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* OCI V2.16 — compact passport alert banner (full card now in My Records) */}
        <PassportAlertBanner userId={user?.id} />

        {/* Today's Performance Panel - Clickable Tiles */}
        <Card className="border-emerald-200 bg-emerald-50 shadow-none" data-testid="today-performance">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Target className="h-5 w-5 text-emerald-600" />
              <h3 className="font-heading font-bold text-slate-900">Today's Performance</h3>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <div className="bg-white p-3 rounded-xl text-center">
                <div className="text-2xl font-bold text-slate-900">{todayTotalConversions}</div>
                <div className="text-xs text-slate-500">Conversions</div>
              </div>
              <div className="bg-white p-3 rounded-xl text-center">
                <div className="text-2xl font-bold text-blue-600">{todayConversionRate}%</div>
                <div className="text-xs text-slate-500">Conv. Rate</div>
              </div>
              
              {/* Lottery Tile - Clickable */}
              <button
                onClick={() => setTodayDetailType('lottery')}
                className="bg-white p-3 rounded-xl text-center hover:bg-violet-50 transition-colors cursor-pointer"
              >
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Ticket className="h-4 w-4 text-violet-500" />
                </div>
                <div className="text-2xl font-bold text-violet-600">
                  {(metrics?.todayLotterySingle || 0) + ((metrics?.todayLotteryDouble || 0) * 2)}
                </div>
                <div className="text-xs text-slate-500">Lottery Entries</div>
                <div className="text-[10px] text-slate-400">{metrics?.todayLotterySingle || 0}S / {metrics?.todayLotteryDouble || 0}D</div>
              </button>
              
              {/* Regular Tile - Clickable */}
              <button
                onClick={() => setTodayDetailType('regular')}
                className="bg-white p-3 rounded-xl text-center hover:bg-emerald-50 transition-colors cursor-pointer"
              >
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Heart className="h-4 w-4 text-emerald-500" />
                </div>
                <div className="text-2xl font-bold text-emerald-600">{metrics?.todayRegularCount || 0}</div>
                <div className="text-xs text-slate-500">Regular</div>
              </button>
              
              {/* One-off Tile - Clickable */}
              <button
                onClick={() => setTodayDetailType('oneoff')}
                className="bg-white p-3 rounded-xl text-center hover:bg-amber-50 transition-colors cursor-pointer"
              >
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Gift className="h-4 w-4 text-amber-500" />
                </div>
                <div className="text-2xl font-bold text-amber-600">{metrics?.todayOneOffCount || 0}</div>
                <div className="text-xs text-slate-500">One-off</div>
              </button>
              
              <div className="bg-white p-3 rounded-xl text-center">
                <div className={`text-2xl font-bold ${(metrics?.todayFailedCount || 0) > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                  {(metrics?.todayPendingCount || 0) + (metrics?.todayFailedCount || 0)}
                </div>
                <div className="text-xs text-slate-500">
                  {(metrics?.todayFailedCount || 0) > 0 ? (
                    <span className="text-red-600 flex items-center justify-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Failed
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-1">
                      <Clock className="h-3 w-3" />
                      Pending
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Interaction Tracker - Debounced Counter System */}
        <Card className="border-slate-200 shadow-none" data-testid="interaction-tracker">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-slate-900">Interaction Tracker</h3>
              <Button
                size="sm"
                variant="outline"
                onClick={handleSyncNow}
                disabled={syncing}
                className="h-8 px-3"
                data-testid="sync-now-btn"
              >
                {syncing ? (
                  <span className="h-4 w-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Clock className="h-4 w-4 mr-2" />
                )}
                {syncing ? "Syncing..." : lastSyncTime ? `Synced ${new Date(lastSyncTime).toLocaleTimeString()}` : "Sync Now"}
              </Button>
            </div>
            
            {/* Location Selector - REQUIRED before tracking */}
            <div className="mb-4">
              <Label className="text-slate-600 text-sm font-medium mb-1.5 block">Location *</Label>
              <Select value={selectedLocation || ""} onValueChange={handleLocationChange} disabled={loadingSession}>
                <SelectTrigger className="h-10" data-testid="location-select">
                  <SelectValue placeholder={loadingSession ? "Loading..." : "Select location..."} />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(loc => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {locations.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">No locations available. Ask your manager to add locations.</p>
              )}
            </div>
            
            {/* Stage Counter Buttons - onClick only, with lock */}
            <div className="grid grid-cols-5 gap-2 mb-6">
              {STAGES.map((stage) => (
                <button
                  key={stage.id}
                  onClick={() => handleStageClick(stage.id)}
                  disabled={isSubmitting || loggingStage === stage.id || !selectedCampaign || !selectedLocation}
                  className={`
                    flex flex-col items-center justify-center p-3 rounded-xl transition-all duration-150
                    min-h-[80px] select-none
                    ${loggingStage === stage.id
                      ? 'bg-blue-100 text-blue-600 scale-95'
                      : isSubmitting
                        ? 'bg-slate-100 text-slate-400 cursor-wait'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-700 active:scale-95'
                    }
                    ${(!selectedCampaign || !selectedLocation) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                  data-testid={`stage-btn-${stage.id}`}
                >
                  <span className="text-sm font-semibold">{stage.label}</span>
                  <span className="text-xl font-bold mt-1">({stageCounts[stage.id]})</span>
                </button>
              ))}
            </div>
            
            {/* Tracker Summary — Current Session */}
            <div className="grid grid-cols-4 gap-3 p-4 bg-slate-50 rounded-xl">
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-900">{sessionTotalInteractions}</div>
                <div className="text-xs text-slate-500">Total Interactions</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600">{sessionConversionTotal}</div>
                <div className="text-xs text-slate-500">Conversions</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{sessionConversionRate}%</div>
                <div className="text-xs text-slate-500">Conv. Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-700">{sessionRatio > 0 ? `1 in ${sessionRatio}` : '-'}</div>
                <div className="text-xs text-slate-500">Ratio</div>
              </div>
            </div>
            
            {/* Session Controls */}
            <div className="flex gap-2 mt-4">
              <Button
                size="sm"
                onClick={handleSaveSession}
                disabled={savingSession || (!selectedCampaign || !selectedLocation)}
                className="flex-1 h-9 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="save-session-btn"
              >
                <Save className="h-4 w-4 mr-1.5" />
                {savingSession ? "Saving..." : "Save Session"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleViewSessions}
                className="h-9"
                data-testid="view-sessions-btn"
              >
                <History className="h-4 w-4 mr-1.5" />
                History
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ==================== KNOCK SHEET ==================== */}
        <Card className={`border-slate-200 shadow-none transition-all duration-300 ${knockFlash === "success" ? "ring-2 ring-emerald-400 bg-emerald-50/30" : ""}`} data-testid="knock-sheet">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <DoorClosed className="h-5 w-5 text-orange-600" />
                <h3 className="font-heading font-bold text-slate-900">Knock Sheet</h3>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setKnockShowCallbacks(v => !v)} data-testid="knock-callbacks-toggle">
                  <PhoneCall className="h-3 w-3 mr-1" />
                  Callbacks{knockCallbacks.length > 0 ? ` (${knockCallbacks.length})` : ""}
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setKnockShowList(v => !v)} data-testid="knock-list-toggle">
                  {knockShowList ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
                  List{knockList.length > 0 ? ` (${knockList.length})` : ""}
                </Button>
              </div>
            </div>

            {!selectedLocation && (
              <div className="text-sm text-amber-600 mb-3 p-3 bg-amber-50 rounded-lg" data-testid="knock-no-location">Select a location above to start knocking</div>
            )}

            {selectedLocation && (
              <>
                {/* Road + House input row */}
                <div className="flex gap-2 mb-3">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={knockRoad}
                      onChange={(e) => setKnockRoad(e.target.value)}
                      placeholder="Road name..."
                      className="w-full h-12 px-3 text-base rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                      data-testid="knock-road-input"
                      autoComplete="off"
                    />
                    {knockRoad.trim() && filteredRoads.length > 0 && filteredRoads[0].toLowerCase() !== knockRoad.toLowerCase() && (
                      <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto" data-testid="knock-road-autocomplete">
                        {filteredRoads.slice(0, 8).map(r => (
                          <button key={r} className="w-full text-left px-3 py-2.5 text-sm hover:bg-slate-50 active:bg-slate-100" onClick={() => { setKnockRoad(r); }}>
                            {r}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <input
                    type="text"
                    value={knockHouse}
                    onChange={(e) => setKnockHouse(e.target.value)}
                    placeholder="No."
                    className="w-20 h-12 px-3 text-center text-base rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    data-testid="knock-house-input"
                  />
                </div>

                {/* Outcome buttons — large, thumb-friendly */}
                <div className="grid grid-cols-5 gap-1.5" data-testid="knock-outcome-buttons">
                  <button
                    onClick={() => handleKnock("no_answer")}
                    disabled={knockSaving || !knockRoad.trim()}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-slate-100 hover:bg-slate-200 active:scale-95 transition-all min-h-[68px] disabled:opacity-40 disabled:pointer-events-none select-none"
                    data-testid="knock-btn-no-answer"
                  >
                    <DoorClosed className="h-5 w-5 text-slate-600 mb-1" />
                    <span className="text-[11px] font-semibold text-slate-700 leading-tight text-center">No Answer</span>
                  </button>
                  <button
                    onClick={() => handleKnock("not_interested")}
                    disabled={knockSaving || !knockRoad.trim()}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-red-50 hover:bg-red-100 active:scale-95 transition-all min-h-[68px] disabled:opacity-40 disabled:pointer-events-none select-none"
                    data-testid="knock-btn-not-interested"
                  >
                    <Ban className="h-5 w-5 text-red-500 mb-1" />
                    <span className="text-[11px] font-semibold text-red-700 leading-tight text-center">Not Int.</span>
                  </button>
                  <button
                    onClick={() => setKnockShowCallbackOptions(v => !v)}
                    disabled={knockSaving || !knockRoad.trim()}
                    className={`flex flex-col items-center justify-center p-3 rounded-xl active:scale-95 transition-all min-h-[68px] disabled:opacity-40 disabled:pointer-events-none select-none ${knockShowCallbackOptions ? "bg-amber-200 ring-2 ring-amber-400" : "bg-amber-50 hover:bg-amber-100"}`}
                    data-testid="knock-btn-callback"
                  >
                    <PhoneCall className="h-5 w-5 text-amber-600 mb-1" />
                    <span className="text-[11px] font-semibold text-amber-700 leading-tight text-center">Callback</span>
                  </button>
                  <button
                    onClick={() => handleKnock("converted")}
                    disabled={knockSaving || !knockRoad.trim()}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-emerald-50 hover:bg-emerald-100 active:scale-95 transition-all min-h-[68px] disabled:opacity-40 disabled:pointer-events-none select-none"
                    data-testid="knock-btn-converted"
                  >
                    <Check className="h-5 w-5 text-emerald-600 mb-1" />
                    <span className="text-[11px] font-semibold text-emerald-700 leading-tight text-center">Converted</span>
                  </button>
                  <button
                    onClick={() => setKnockShowOtherInput(v => !v)}
                    disabled={knockSaving || !knockRoad.trim()}
                    className={`flex flex-col items-center justify-center p-3 rounded-xl active:scale-95 transition-all min-h-[68px] disabled:opacity-40 disabled:pointer-events-none select-none ${knockShowOtherInput ? "bg-blue-200 ring-2 ring-blue-400" : "bg-blue-50 hover:bg-blue-100"}`}
                    data-testid="knock-btn-other"
                  >
                    <HelpCircle className="h-5 w-5 text-blue-500 mb-1" />
                    <span className="text-[11px] font-semibold text-blue-700 leading-tight text-center">Other</span>
                  </button>
                </div>

                {/* Callback inline options */}
                {knockShowCallbackOptions && (
                  <div className="flex gap-2 mt-2 animate-in fade-in duration-200" data-testid="knock-callback-options">
                    <button onClick={() => handleCallbackQuick("later")} className="flex-1 h-10 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 text-sm font-medium active:scale-95 transition-all">
                      Later Today
                    </button>
                    <button onClick={() => handleCallbackQuick("tomorrow")} className="flex-1 h-10 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 text-sm font-medium active:scale-95 transition-all">
                      Tomorrow
                    </button>
                    <input
                      type="datetime-local"
                      className="flex-1 h-10 px-2 text-sm rounded-lg border border-amber-300 focus:ring-1 focus:ring-amber-500 outline-none"
                      onChange={(e) => {
                        if (e.target.value) handleKnock("callback", { callbackDateTime: new Date(e.target.value).toISOString() });
                      }}
                      data-testid="knock-callback-picker"
                    />
                  </div>
                )}

                {/* Other note input */}
                {knockShowOtherInput && (
                  <div className="flex gap-2 mt-2 animate-in fade-in duration-200" data-testid="knock-other-input">
                    <input
                      type="text"
                      value={knockOtherNote}
                      onChange={(e) => setKnockOtherNote(e.target.value)}
                      placeholder="Note (optional)..."
                      className="flex-1 h-10 px-3 text-sm rounded-lg border border-slate-300 focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                    <button onClick={() => handleKnock("other", { outcomeNote: knockOtherNote || null })} className="h-10 px-4 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 active:scale-95 transition-all" data-testid="knock-other-save">
                      Save
                    </button>
                  </div>
                )}

                {/* Success flash */}
                {knockFlash === "success" && (
                  <div className="flex items-center justify-center gap-2 mt-2 text-emerald-600 font-semibold text-sm animate-in fade-in zoom-in duration-200" data-testid="knock-success-flash">
                    <Check className="h-5 w-5" /> Saved
                  </div>
                )}
              </>
            )}

            {/* Callbacks panel */}
            {knockShowCallbacks && knockCallbacks.length > 0 && (
              <div className="mt-4 border-t border-slate-200 pt-3" data-testid="knock-callbacks-list">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Upcoming Callbacks</h4>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {knockCallbacks.map(cb => (
                    <div key={cb.id} className="flex items-center justify-between p-2.5 bg-amber-50 rounded-lg text-sm">
                      <div>
                        <span className="font-medium text-slate-900">{cb.road} {cb.houseNumber}</span>
                        <span className="text-xs text-slate-500 ml-2">{cb.postcode}</span>
                      </div>
                      <span className="text-xs font-mono text-amber-700">
                        {cb.callbackDateTime ? new Date(cb.callbackDateTime).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {knockShowCallbacks && knockCallbacks.length === 0 && (
              <div className="mt-4 text-sm text-slate-500 italic text-center">No callbacks scheduled</div>
            )}

            {/* Knock list panel */}
            {knockShowList && (
              <div className="mt-4 border-t border-slate-200 pt-3" data-testid="knock-list-panel">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Previous Knocks</h4>
                {knockList.length === 0 ? (
                  <div className="text-sm text-slate-500 italic text-center">No previous knocks at this location</div>
                ) : (
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {knockList.map(k => (
                      <div key={k.id} className={`flex items-center justify-between p-2.5 rounded-lg text-sm ${k.recentVisit ? "bg-blue-50 border border-blue-100" : "bg-slate-50"}`} data-testid={`knock-list-item-${k.id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium text-slate-900 truncate">{k.road} {k.houseNumber}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                            k.outcome === "converted" ? "bg-emerald-100 text-emerald-700" :
                            k.outcome === "callback" ? "bg-amber-100 text-amber-700" :
                            k.outcome === "not_interested" ? "bg-red-100 text-red-700" :
                            k.outcome === "no_answer" ? "bg-slate-200 text-slate-600" :
                            "bg-blue-100 text-blue-700"
                          }`}>
                            {k.outcome?.replace("_", " ")}
                          </span>
                          {k.recentVisit && <span className="text-[9px] text-blue-600 font-medium">RECENT</span>}
                        </div>
                        <button onClick={() => handleDoNotKnock(k.id)} className="text-xs text-red-500 hover:text-red-700 hover:underline flex-shrink-0 ml-2" data-testid={`knock-dnk-${k.id}`}>
                          DNK
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* OCI V2 — Gift Aid Performance Strip (fundraiser-scoped) */}
        <GiftAidMetricsStrip variant="fundraiser" fundraiserId={user?.id} />

        {/* Key Stats - UPDATED METRICS (Part 5) */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-4 text-center">
              <Users className="h-5 w-5 text-emerald-600 mx-auto mb-2" />
              <div className="font-heading font-bold text-2xl text-slate-900">{metrics?.totalConversions || 0}</div>
              <div className="text-xs text-slate-500">All-Time Signups</div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-4 text-center">
              <Calendar className="h-5 w-5 text-blue-600 mx-auto mb-2" />
              <div className="font-heading font-bold text-2xl text-slate-900">{metrics?.avgAge?.toFixed(0) || '-'}</div>
              <div className="text-xs text-slate-500">Avg Age</div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-4 text-center">
              <DollarSign className="h-5 w-5 text-violet-600 mx-auto mb-2" />
              <div className="font-heading font-bold text-2xl text-slate-900">£{metrics?.avgRegularDonation?.toFixed(0) || 0}</div>
              <div className="text-xs text-slate-500">Avg Gift</div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-4 text-center">
              <TrendingDown className="h-5 w-5 text-amber-600 mx-auto mb-2" />
              <div className="font-heading font-bold text-2xl text-slate-900">{((metrics?.noShowRate || 0) * 100).toFixed(0)}%</div>
              <div className="text-xs text-slate-500">No-Show Rate</div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-4 text-center">
              <AlertCircle className="h-5 w-5 text-red-500 mx-auto mb-2" />
              <div className="font-heading font-bold text-2xl text-slate-900">{((metrics?.cancellation30d || 0) * 100).toFixed(0)}%</div>
              <div className="text-xs text-slate-500">Cancel Rate</div>
            </CardContent>
          </Card>
        </div>

        {/* Insights */}
        {metrics?.insights && metrics.insights.length > 0 && (
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Lightbulb className="h-5 w-5 text-amber-500" />
                <h3 className="font-heading font-bold text-slate-900">Insights</h3>
              </div>
              <div className="space-y-2">
                {metrics.insights.map((insight, i) => (
                  <div key={`insight-${i}`} className="p-3 bg-amber-50 rounded-lg text-amber-800 text-sm">{insight}</div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

      </main>

      <Dialog open={showDonationTypeModal} onOpenChange={setShowDonationTypeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select Donation Type</DialogTitle>
          </DialogHeader>
          {selectedCampaignObj?.id && (
            <PaymentReadinessBanner campaignId={selectedCampaignObj.id} className="mb-1" />
          )}
          <div className="grid grid-cols-1 gap-3 py-4">
            {selectedCampaignObj?.enableLottery !== false && (
            <button
              onClick={() => startConversionWithType('lottery')}
              className="flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-violet-400 hover:bg-violet-50 transition-colors text-left"
              data-testid="type-lottery-btn"
            >
              <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                <Ticket className="h-6 w-6 text-violet-600" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-slate-900">Lottery</div>
                <div className="text-sm text-slate-500">
                  Single £{selectedCampaignObj?.lotterySinglePrice?.toFixed(2) || '5.00'} / Double £{selectedCampaignObj?.lotteryDoublePrice?.toFixed(2) || '10.00'}
                </div>
              </div>
            </button>
            )}
            
            {selectedCampaignObj?.enableRegularGiving !== false && (
            <button
              onClick={() => startConversionWithType('regular_giving')}
              className="flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 transition-colors text-left"
              data-testid="type-regular-btn"
            >
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Heart className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-slate-900">Regular Giving</div>
                <div className="text-sm text-slate-500">Monthly donation</div>
              </div>
            </button>
            )}
            
            {selectedCampaignObj?.enableOneOff !== false && (
            <button
              onClick={() => startConversionWithType('one_off')}
              className="flex items-center gap-4 p-4 rounded-xl border-2 border-slate-200 hover:border-amber-400 hover:bg-amber-50 transition-colors text-left"
              data-testid="type-oneoff-btn"
            >
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Gift className="h-6 w-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-slate-900">One-Off</div>
                <div className="text-sm text-slate-500">Single donation</div>
              </div>
            </button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Today Detail Modal */}
      <Dialog open={!!todayDetailType} onOpenChange={() => setTodayDetailType(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {todayDetailType === 'lottery' && 'Lottery Today'}
              {todayDetailType === 'regular' && 'Regular Giving Today'}
              {todayDetailType === 'oneoff' && 'One-Off Today'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {todayDetailType === 'lottery' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-xl text-center">
                    <div className="text-3xl font-bold text-violet-600">{metrics?.todayLotterySingle || 0}</div>
                    <div className="text-sm text-slate-500">Single</div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl text-center">
                    <div className="text-3xl font-bold text-violet-600">{metrics?.todayLotteryDouble || 0}</div>
                    <div className="text-sm text-slate-500">Double</div>
                  </div>
                </div>
                <div className="p-4 bg-violet-50 rounded-xl text-center">
                  <div className="text-2xl font-bold text-violet-700">
                    {(metrics?.todayLotterySingle || 0) + ((metrics?.todayLotteryDouble || 0) * 2)}
                  </div>
                  <div className="text-sm text-violet-600">Total Lottery Entries</div>
                </div>
                <div className="grid grid-cols-2 gap-4 border-t pt-4">
                  <div className="text-center">
                    <div className="text-lg font-semibold text-slate-700">£{metrics?.avgLotteryValue?.toFixed(0) || 12}</div>
                    <div className="text-xs text-slate-500">Avg Value</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-violet-700">
                      £{(((metrics?.todayLotterySingle || 0) * 12) + ((metrics?.todayLotteryDouble || 0) * 24)).toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-500">Est. Total/mo</div>
                  </div>
                </div>
              </>
            )}
            {todayDetailType === 'regular' && (
              <>
                <div className="p-6 bg-emerald-50 rounded-xl text-center">
                  <div className="text-4xl font-bold text-emerald-600">{metrics?.todayRegularCount || 0}</div>
                  <div className="text-sm text-emerald-700 mt-2">Regular Giving Signups</div>
                </div>
                <div className="grid grid-cols-2 gap-4 border-t pt-4">
                  <div className="text-center">
                    <div className="text-lg font-semibold text-emerald-600">£{metrics?.avgRegularDonation?.toFixed(0) || 10}</div>
                    <div className="text-xs text-slate-500">Avg/month</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-emerald-700">
                      £{((metrics?.todayRegularCount || 0) * (metrics?.avgRegularDonation || 10)).toFixed(0)}
                    </div>
                    <div className="text-xs text-slate-500">Total Value/mo</div>
                  </div>
                </div>
              </>
            )}
            {todayDetailType === 'oneoff' && (
              <>
                <div className="p-6 bg-amber-50 rounded-xl text-center">
                  <div className="text-4xl font-bold text-amber-600">{metrics?.todayOneOffCount || 0}</div>
                  <div className="text-sm text-amber-700 mt-2">One-Off Donations</div>
                </div>
                <div className="grid grid-cols-2 gap-4 border-t pt-4">
                  <div className="text-center">
                    <div className="text-lg font-semibold text-amber-600">£{metrics?.avgOneOffDonation?.toFixed(0) || 25}</div>
                    <div className="text-xs text-slate-500">Avg Value</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-amber-700">
                      £{metrics?.todayOneOffTotal?.toFixed(0) || ((metrics?.todayOneOffCount || 0) * (metrics?.avgOneOffDonation || 25)).toFixed(0)}
                    </div>
                    <div className="text-xs text-slate-500">Total Value</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Session History Dialog */}
      <Dialog open={showSessionHistory} onOpenChange={setShowSessionHistory}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="session-history-dialog">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-slate-600" />
              Session History
            </DialogTitle>
            
            {/* Date Filter Controls */}
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">View:</span>
                <select
                  value={historyStartDate && historyEndDate ? "custom" : historyDateRange}
                  onChange={(e) => {
                    if (e.target.value === "custom") return;
                    setHistoryDateRange(Number(e.target.value));
                    setHistoryStartDate("");
                    setHistoryEndDate("");
                  }}
                  className="h-8 px-2 text-sm border border-slate-200 rounded-lg bg-white"
                  data-testid="history-date-range"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={14}>Last 14 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                  <option value="custom" disabled={!historyStartDate}>Custom range</option>
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={historyStartDate}
                  onChange={(e) => setHistoryStartDate(e.target.value)}
                  className="h-8 px-2 text-sm border border-slate-200 rounded-lg"
                  placeholder="From"
                />
                <span className="text-slate-400">–</span>
                <input
                  type="date"
                  value={historyEndDate}
                  onChange={(e) => setHistoryEndDate(e.target.value)}
                  className="h-8 px-2 text-sm border border-slate-200 rounded-lg"
                  placeholder="To"
                />
              </div>
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto py-4 space-y-6">
            {(() => {
              const groupedSessions = getGroupedSessions();
              
              if (groupedSessions.length === 0) {
                return (
                  <div className="text-center py-12 text-slate-500">
                    <History className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">No sessions found</p>
                    <p className="text-sm mt-1">Try adjusting your date range</p>
                  </div>
                );
              }
              
              return groupedSessions.map((day) => (
                <div key={day.date} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`day-group-${day.date}`}>
                  {/* Day Header with Summary */}
                  <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-slate-900">
                        <Calendar className="h-4 w-4 inline mr-2 text-slate-500" />
                        {new Date(day.date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                      <div className="text-sm text-slate-600">
                        {day.sessions.length} session{day.sessions.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                    
                    {/* Daily Summary */}
                    <div className="mt-2 flex items-center gap-4 text-sm">
                      <span className="text-slate-600">
                        <span className="font-semibold text-slate-900">{day.totalInteractions}</span> interactions
                      </span>
                      <span className="text-slate-400">|</span>
                      <span className="text-emerald-600">
                        <span className="font-semibold">{day.totalConversions}</span> conversions
                      </span>
                      <span className="text-slate-400">|</span>
                      <span className="text-blue-600 font-semibold">
                        {day.conversionRate}%
                      </span>
                    </div>
                  </div>
                  
                  {/* Sessions Table */}
                  <div className="divide-y divide-slate-100">
                    {/* Table Header */}
                    <div className="grid grid-cols-4 gap-2 px-4 py-2 bg-slate-50/50 text-xs font-medium text-slate-500 uppercase tracking-wide">
                      <div>Time</div>
                      <div className="text-center">Interactions</div>
                      <div className="text-center">Conversions</div>
                      <div className="text-center">Rate</div>
                    </div>
                    
                    {/* Session Rows */}
                    {day.sessions.map((session) => (
                      <div 
                        key={session.id} 
                        className="grid grid-cols-4 gap-2 px-4 py-3 hover:bg-slate-50 transition-colors"
                        data-testid={`session-row-${session.id}`}
                      >
                        <div className="text-sm text-slate-700 font-medium">
                          {formatSessionTime(session.createdAt)}
                        </div>
                        <div className="text-center text-sm font-semibold text-slate-900">
                          {session.totalInteractions}
                        </div>
                        <div className="text-center text-sm font-semibold text-emerald-600">
                          {session.totalConversions}
                        </div>
                        <div className="text-center text-sm font-semibold text-blue-600">
                          {session.conversionRate}%
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ));
            })()}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
